import { LoadingIndicatorComponent } from '../../components/loading-indicator/loading-indicator.component';
import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SankhyaGatewayService } from '../../services/sankhya-gateway.service';
import { AuthService } from '../../services/auth.service';
import { EmpresaStateService } from '../../services/empresa-state.service';
import { PermissaoAcessoService } from '../../services/permissao-acesso.service';
import { AerosysGatewayService, AerosysEmpresa, AerosysVendaConferencia } from '../../services/aerosys-gateway.service';
import { Subscription, interval } from 'rxjs';

export interface ConferenciaLinha {
  nunota: number;
  dtneg?: string;
  prioridade: string;
  imediato: string;
  nomeCliente: string;
  vendedor: string;
  status: string;
  prefixo?: string;
  confCodusu: number;
  confNome: string;
  dataInicio: string;
  qtdConferidos: number;
  totalItens: number;
  retornouPendencia?: boolean;
  numTag?: string;
  /** Itens não conferidos cuja pendência "Item no outro Galpão" já foi
   *  resolvida (voltou do outro galpão) -- acende o sininho no card (ver
   *  ITENS_PENDENCIA_RESOLVIDA em ConferenciaComponent, GPL-Integracao backend). */
  itensPendenciaResolvida?: number;
  /** Origem do dado -- 'sankhya' (padrão, campo ausente) ou 'aerosys'
   *  (Sistema Legado -- ver bloco "Vendas do Sistema Legado" abaixo). */
  origem?: 'sankhya' | 'aerosys';
  /** Número de título exibido pro Sistema Legado -- nunota aqui é sintético
   *  (negativo, ver _mapLegadoParaLinha), então o número real pra mostrar
   *  na tela vem deste campo. */
  numTituloLegado?: string;
}

@Component({
  selector: 'app-estoque-conferencia',
  standalone: true,
  imports: [LoadingIndicatorComponent, CommonModule, FormsModule],
  templateUrl: './estoque-conferencia.component.html',
  styleUrls: ['./estoque-conferencia.component.css']
})
export class EstoqueConferenciaComponent implements OnInit, OnDestroy {
  pedidos: ConferenciaLinha[] = [];
  carregando = false;
  erro = '';
  private sub?: Subscription;
  private autoRefreshSub?: Subscription;
  private _empresaSub?: Subscription;

  @Output() iniciar    = new EventEmitter<number>();
  @Output() finalizado = new EventEmitter<void>();
  /** "Continuar Conferência" clicado num card do Sistema Legado (Aerosys) — emite
   *  o id real da venda no Aerosys (recuperado de -Math.abs(nunota), ver
   *  _mapLegadoParaLinha) e o número exibido, para a tela de itens legado. */
  @Output() iniciarLegado = new EventEmitter<{ idVenda: number; numVenda?: string }>();

  iniciandoNunota:   number | null = null;
  finalizandoNunota: number | null = null;
  erroIniciar        = '';
  erroFinalizar      = '';
  codusuAtual        = 0;

  // ── Estornar para SEPARACAO ──────────────────────────────
  estornandoNunota: number | null = null;
  erroEstornar = '';
  confirmandoEstorno: ConferenciaLinha | null = null;

  // ── Vendas do Sistema Legado (Aerosys) — fonte alternativa dos cards ────
  // Liberado por permissão própria (CONAER no resource GPL_ConferenciaEstoque
  // — diferente da PAIAER do Painel de Conferência, cada tela com sua sigla).
  // Mesmo padrão de "sem toggle manual" do Aplicativo de Separação (ver
  // estoque-separacao.component.ts): quem TEM a permissão vê SOMENTE os
  // cards das vendas do Aerosys aqui; quem NÃO tem vê SOMENTE os pedidos do
  // Sankhya, nunca as duas juntas.
  podeVerVendasLegado = false;
  legadoAberto = false;
  legadoEmpresas: AerosysEmpresa[] = [];
  legadoEmpresasCarregando = false;
  legadoEmpresasErro = '';
  legadoEmpresaSelecionada: AerosysEmpresa | null = null;
  legadoLista: AerosysVendaConferencia[] = [];
  legadoCarregando = false;
  legadoErro = '';

  /** Mesma lista de empresas do Aerosys usadas nas outras telas do Sistema Legado. */
  private readonly LEGADO_EMPRESAS_PERMITIDAS = [1, 3, 5, 7, 8, 9, 13, 15, 17];
  private readonly LEGADO_PREF_STORAGE_KEY = 'gpl_estoque_conferencia_legado_pref';

  /** Fonte de dados dos cards: com a permissão CONAER, SOMENTE vendas do
   *  Aerosys; sem a permissão, SOMENTE pedidos do Sankhya — nunca as duas
   *  juntas (mesma regra do Aplicativo de Separação). */
  get linhas(): ConferenciaLinha[] {
    if (this.legadoAberto) {
      return this.legadoLista.map(v => this._mapLegadoParaLinha(v));
    }
    return this.pedidos;
  }

  get carregandoLinhas(): boolean {
    return this.legadoAberto ? this.legadoCarregando : this.carregando;
  }

  get aguardandoEscolhaEmpresaLegado(): boolean {
    return this.legadoAberto && !this.legadoEmpresaSelecionada;
  }

  constructor(
    private gateway: SankhyaGatewayService,
    private auth: AuthService,
    private aerosysGateway: AerosysGatewayService,
    private permissaoAcesso: PermissaoAcessoService,
    readonly empresaState: EmpresaStateService
  ) {}

  async ngOnInit(): Promise<void> {
    this.codusuAtual = await this.auth.getCodusuNumerico();
    this.buscarPedidos(true);
    this.autoRefreshSub = interval(10000).subscribe(() => this.buscarPedidos(false));

    // Isolamento por empresa: recarrega ao trocar a empresa selecionada.
    this._empresaSub = this.empresaState.empresaMudou.subscribe(() => {
      this.buscarPedidos(true);
    });

    this.permissaoAcesso.podeVerVendasLegadoConferencia().then(pode => {
      this.podeVerVendasLegado = pode;
      this.legadoAberto = pode;
      if (pode) {
        this.carregarLegadoEmpresas();
      }
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.autoRefreshSub?.unsubscribe();
    this._empresaSub?.unsubscribe();
  }

  /** Nome de exibição da empresa atualmente selecionada (badge no cabeçalho). */
  get nomeEmpresaAtual(): string {
    const emp = this.empresaState.opcoes.find(e => e.codEmp === this.empresaState.selecionada);
    return emp?.nomeFant || emp?.razaoAbrev || '';
  }

  buscarPedidos(exibirLoading: boolean = false): void {
    if (exibirLoading) {
      this.carregando = true;
      this.erro = '';
    }
    if (this.sub && !this.sub.closed) this.sub.unsubscribe();

    const codEmp = Number(this.empresaState.selecionada ?? 1);
    this.sub = this.gateway.buscarPedidosConferenciaEstoque(this.codusuAtual, codEmp).subscribe({
      next: (res: any) => {
        this.carregando = false;
        if (res.sucesso) {
          this.erro = '';
          this.pedidos = res.pedidos as ConferenciaLinha[];
        } else if (exibirLoading || !this.pedidos.length) {
          this.erro = res.erro || 'Erro ao carregar pedidos.';
        }
      },
      error: (err: any) => {
        this.carregando = false;
        if (exibirLoading || !this.pedidos.length) {
          this.erro = 'Falha de comunicação com o servidor.';
        }
        console.error(err);
      }
    });
  }

  /**
   * "Em conferência" -- pro Sankhya, VEN_STATUS muda pra CONFERENCIA e só
   * conta como "minha" se confCodusu bater com o usuário logado. Pro
   * Sistema Legado é diferente: VEN_STATUS continua "SEPARACAO TOTAL" o
   * tempo todo (só muda em PRONTO/ESTOQUE ao finalizar -- ver comentário em
   * VendaConferenciaLegadoRepository, projeto Aerosys), então "em
   * conferência" ali é detectado pela existência de uma sessão ativa
   * (conferenteNome preenchido) -- sem validação de posse, igual à
   * Separação: qualquer usuário identificado pode continuar.
   */
  emConferencia(pedido: ConferenciaLinha): boolean {
    if (pedido.origem === 'aerosys') {
      return !!(pedido.confNome && pedido.confNome.trim());
    }
    const st = pedido.status?.toUpperCase() || '';
    return (st === 'CONFERENCIA' || st === 'CONFERÊNCIA') && pedido.confCodusu === this.codusuAtual;
  }

  /** Clique em "Iniciar/Continuar Conferência" num card do Sistema Legado -- iniciar() do Aerosys é idempotente (ver ConferenciaLegadoService), então "iniciar" e "continuar" são a mesma navegação. */
  continuarOuIniciarLegado(pedido: ConferenciaLinha): void {
    this.iniciarLegado.emit({ idVenda: Math.abs(pedido.nunota), numVenda: pedido.numTituloLegado });
  }

  iniciarConferencia(pedido: ConferenciaLinha): void {
    if (pedido.origem === 'aerosys') return; // usa continuarOuIniciarLegado — botão fica escondido, ver template.
    if (this.iniciandoNunota !== null) return;
    this.iniciandoNunota = pedido.nunota;
    this.erroIniciar = '';
    this.gateway.iniciarConferencia(pedido.nunota).subscribe({
      next: (res: any) => {
        this.iniciandoNunota = null;
        if (res?.sucesso) {
          this.iniciar.emit(pedido.nunota);
        } else {
          this.erroIniciar = res?.erro || 'Erro ao iniciar conferência.';
        }
      },
      error: () => {
        this.iniciandoNunota = null;
        this.erroIniciar = 'Falha de comunicação com o servidor.';
      }
    });
  }

  /** Pedido voltou da pendência com todos os itens já conferidos → finaliza direto da lista.
   *  Recurso só do fluxo nativo Sankhya (PENDENCIA VENDEDOR) -- o Sistema
   *  Legado não tem esse conceito, só "Item no outro Galpão" (ver
   *  comentário de topo de estoque-conferencia-itens-legado.component.ts). */
  podeFinalizarDireto(pedido: ConferenciaLinha): boolean {
    if (pedido.origem === 'aerosys') return false;
    return this.emConferencia(pedido)
      && !!pedido.retornouPendencia
      && pedido.totalItens > 0
      && pedido.qtdConferidos >= pedido.totalItens;
  }

  finalizarDiretamente(pedido: ConferenciaLinha): void {
    if (this.finalizandoNunota !== null) return;
    this.finalizandoNunota = pedido.nunota;
    this.erroFinalizar = '';
    this.gateway.finalizarConferencia(pedido.nunota).subscribe({
      next: (res: any) => {
        this.finalizandoNunota = null;
        if (res?.sucesso) {
          this.finalizado.emit();
        } else {
          this.erroFinalizar = res?.erro || 'Erro ao finalizar conferência.';
        }
      },
      error: () => {
        this.finalizandoNunota = null;
        this.erroFinalizar = 'Falha de comunicação com o servidor.';
      }
    });
  }

  solicitarEstornar(event: Event, pedido: ConferenciaLinha): void {
    event.stopPropagation();
    if (pedido.origem === 'aerosys') return; // ver template — botão nem aparece pra vendas do Aerosys.
    if (this.estornandoNunota !== null) return;
    this.confirmandoEstorno = pedido;
    this.erroEstornar = '';
  }

  cancelarEstornar(): void {
    if (this.estornandoNunota !== null) return;
    this.confirmandoEstorno = null;
    this.erroEstornar = '';
  }

  confirmarEstornar(): void {
    const pedido = this.confirmandoEstorno;
    if (!pedido || this.estornandoNunota !== null) return;
    this.estornandoNunota = pedido.nunota;
    this.erroEstornar = '';
    this.gateway.estornarConferencia(pedido.nunota).subscribe({
      next: (res: any) => {
        this.estornandoNunota = null;
        if (res?.sucesso) {
          this.confirmandoEstorno = null;
          this.buscarPedidos(false);
        } else {
          this.erroEstornar = res?.erro || 'Erro ao estornar conferência.';
        }
      },
      error: () => {
        this.estornandoNunota = null;
        this.erroEstornar = 'Falha de comunicação com o servidor.';
      }
    });
  }

  // ── Vendas do Sistema Legado (Aerosys) — fonte alternativa dos cards ────
  // Bloco isolado de propósito (mesmo comentário do Aplicativo de Separação)
  // — todo ele pode ser removido junto com a permissão CONAER quando o
  // Aerosys for desativado, sem afetar o restante da Conferência de Estoque.

  /** Converte uma venda "rica" do Aerosys (AerosysVendaConferencia) para o
   *  mesmo formato de linha usado pelos pedidos do Sankhya — mesma função
   *  usada no Painel de Conferência (paineis-conferencia.component.ts),
   *  duplicada aqui de propósito pra manter as duas telas independentes e
   *  fáceis de remover isoladamente. */
  private _mapLegadoParaLinha(v: AerosysVendaConferencia): ConferenciaLinha {
    const imediatoTruthy = ['S', 'SIM', 'TRUE', '1'].includes((v.fatImediato ?? '').toUpperCase());
    return {
      nunota: -Math.abs(v.id),
      dtneg: this._formatarDataBr(v.dataEmissao, false),
      prioridade: v.prioridade || 'Normal',
      imediato: imediatoTruthy ? 'Sim' : 'Não',
      nomeCliente: v.clienteNome || '—',
      vendedor: v.vendedorNome || '—',
      status: v.status || '—',
      prefixo: v.prefixoAeronave || undefined,
      confCodusu: 0, // nunca "meu" via codusu do Sankhya — emConferencia() trata origem==='aerosys' à parte.
      confNome: v.conferenteNome || '',
      dataInicio: '', // AerosysVendaConferencia não expõe início de sessão no painel (ver comentário no service).
      qtdConferidos: v.itensConferidos ?? 0,
      totalItens: v.totalItens ?? 0,
      itensPendenciaResolvida: v.itensPendenciaResolvida ?? 0,
      origem: 'aerosys',
      numTituloLegado: v.numTitulo || String(v.id),
    };
  }

  /** Normaliza uma data (ISO ou epoch) para DD/MM/YYYY — mesma lógica das outras telas do Sistema Legado. */
  private _formatarDataBr(valor: string | null | undefined, comHora: boolean): string | undefined {
    if (!valor) return undefined;
    const d = new Date(valor);
    if (isNaN(d.getTime())) return undefined;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    if (!comHora) return `${dd}/${mm}/${yyyy}`;
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  }

  carregarLegadoEmpresas(): void {
    this.legadoEmpresasCarregando = true;
    this.legadoEmpresasErro = '';
    this.aerosysGateway.listarEmpresas().subscribe({
      next: (lista) => {
        const todas = Array.isArray(lista) ? lista : [];
        this.legadoEmpresas = todas.filter(e => this.LEGADO_EMPRESAS_PERMITIDAS.includes(Number(e.id)));
        this.legadoEmpresasCarregando = false;

        const empresaLembrada = this._restaurarEmpresaLegado();
        let alvo = empresaLembrada != null
          ? this.legadoEmpresas.find(e => Number(e.id) === empresaLembrada)
          : undefined;
        if (!alvo) {
          alvo = this.legadoEmpresas.find(e => Number(e.id) === 1);
        }
        if (alvo) this.selecionarLegadoEmpresa(alvo, false);
      },
      error: (err: any) => {
        this.legadoEmpresasErro = err?.error?.detail ?? err?.message ?? 'Erro ao carregar empresas do Aerosys.';
        this.legadoEmpresasCarregando = false;
      }
    });
  }

  selecionarLegadoEmpresa(empresa: AerosysEmpresa, persistir = true): void {
    this.legadoEmpresaSelecionada = empresa;
    if (persistir) this._salvarEmpresaLegado(empresa.id);
    this.buscarVendasLegado();
  }

  onLegadoEmpresaSelectChange(id: number | string | null): void {
    if (id == null) return;
    const empresa = this.legadoEmpresas.find(e => Number(e.id) === Number(id));
    if (empresa && Number(empresa.id) !== Number(this.legadoEmpresaSelecionada?.id)) {
      this.selecionarLegadoEmpresa(empresa);
    }
  }

  buscarVendasLegado(): void {
    if (!this.legadoEmpresaSelecionada) return;
    this.legadoCarregando = true;
    this.legadoErro = '';

    this.aerosysGateway.listarConferenciaPainelLegado(this.legadoEmpresaSelecionada.id).subscribe({
      next: (lista) => {
        this.legadoLista = Array.isArray(lista) ? lista : [];
        this.legadoCarregando = false;
      },
      error: (err: any) => {
        this.legadoErro = err?.error?.detail ?? err?.message ?? 'Erro ao buscar Vendas do Sistema Legado.';
        this.legadoCarregando = false;
      }
    });
  }

  /** AOG (vermelho), Urgente (laranja), Alta (vermelho claro) e Normal (verde) --
   *  mesma lógica de cores do Painel de Separação (ver prioridadeClass lá).
   *  Usa "includes" porque o valor cru às vezes vem com prefixo numérico do
   *  Sankhya, ex.: "1-AOG", "3-URGENTE". */
  prioridadeClass(prioridade: string | null | undefined): string {
    const p = (prioridade ?? '').trim().toLowerCase();
    if (p.includes('aog')) return 'badge badge-aog';
    if (p.includes('urgente')) return 'badge badge-urgente';
    if (p.includes('alta')) return 'badge badge-alta';
    return 'badge badge-normal';
  }

  // ── Preferência persistida (localStorage) ────────────────────

  private _salvarEmpresaLegado(empresaId: number | null): void {
    try {
      localStorage.setItem(this.LEGADO_PREF_STORAGE_KEY, JSON.stringify({ empresaId }));
    } catch {
      // localStorage indisponivel (modo privado etc.) - sem persistencia, sem quebrar a tela.
    }
  }

  private _restaurarEmpresaLegado(): number | null {
    try {
      const raw = localStorage.getItem(this.LEGADO_PREF_STORAGE_KEY);
      if (!raw) return null;
      const pref = JSON.parse(raw);
      return typeof pref?.empresaId === 'number' ? pref.empresaId : null;
    } catch {
      return null;
    }
  }
}
